import { ERoles } from '@constants';
import { IFile, IUser } from '@sdk/contracts';
import { Flex, Tooltip, App } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import { InformationBanner } from '../InformationBanner';

import {
  RequirementSemiBold,
  RequirementsWrapper,
  Wrapper,
  TitleWithTooltip,
  TooltipIconWrapper
} from './AttachFiles.styles';

import { Icon } from '@/components/Icon';
import { SingleFileUploader } from '@/components/SingleFileUploader';
import { useMutateUserInfos } from '@/store';
import { useFileService } from '@/store/file/useFileService';
import { useUserInfoByUser } from '@/store/users/user-info/useUserInfo';

type AttachFilesProps = {
  profile?: IUser;
};

export const AttachFiles: React.FC<AttachFilesProps> = ({ profile }) => {
  const { message } = App.useApp();
  const { t } = useTranslation('StudentProfileConfigurationTranslations');
  const { t: tDefault } = useTranslation('DefaultTranslations');
  const { updateUserInfo } = useMutateUserInfos(profile?._id || '');
  const { userInfo } = useUserInfoByUser(profile?._id || '');
  const { getFileWithUrl } = useFileService();

  const handleFileSubmit = (fieldName: string) => async (fileId?: string) => {
    let fileValue: IFile | undefined = undefined;
    if (fileId) {
      try {
        const loaded = await getFileWithUrl(fileId);
        if (!loaded) {
          message.error(tDefault('SOMETHING_WENT_WRONG'));
          return;
        }
        fileValue = loaded;
      } catch (error) {
        message.error(tDefault('SOMETHING_WENT_WRONG'));
        return;
      }
    }

    updateUserInfo(
      {
        ...userInfo,
        [fieldName]: fileValue
      },
      {
        onSuccess: () => {
          message.success(t('SUCCESS_SAVED_CHANGES'));
        },
        onError: () => {
          message.error(tDefault('SOMETHING_WENT_WRONG'));
        }
      }
    );
  };
  const isStudent = profile?.role === ERoles.STUDENT;

  return (
    <Wrapper>
      <SingleFileUploader
        title={t('DNI_FILE')}
        fieldName="identityDocFile"
        onSubmit={handleFileSubmit('identityDocFile')}
        acceptedTypes={['.pdf']}
        maxSizeInMB={10}
        initialFileId={userInfo?.identityDocFile?._id}
        blockAfterSubmit={isStudent}
        requirements={
          <RequirementsWrapper>
            <ul>
              <li>
                <Trans
                  i18nKey="StudentProfileConfigurationTranslations:DNI_FILE_REQUIREMENTS"
                  components={{ strong: <RequirementSemiBold /> }}
                />
              </li>
              <li>
                <Trans
                  i18nKey="StudentProfileConfigurationTranslations:PASSPORT_FILE_DESC"
                  components={{ strong: <RequirementSemiBold /> }}
                />
              </li>
            </ul>
          </RequirementsWrapper>
        }
      />

      <SingleFileUploader
        title={
          <TitleWithTooltip>
            {t('DEGREE_FILE')}
            <Tooltip title={t('DEGREE_FILE_TOOLTIP')}>
              <TooltipIconWrapper>
                <Icon i="InformationFilled" color="black9" size="20" />
              </TooltipIconWrapper>
            </Tooltip>
          </TitleWithTooltip>
        }
        titleText={t('DEGREE_FILE')}
        fieldName="degreeFile"
        onSubmit={handleFileSubmit('degreeFile')}
        acceptedTypes={['.pdf']}
        maxSizeInMB={10}
        initialFileId={userInfo?.degreeFile?._id}
        blockAfterSubmit={isStudent}
        requirements={
          <Flex vertical gap={16}>
            <RequirementsWrapper>
              <>
                <RequirementSemiBold>{t('DEGREE_FILE_DESC_1')}</RequirementSemiBold>
                <ul>
                  <li>{t('DEGREE_FILE_REQUIREMENTS_1')}</li>
                </ul>
              </>
              <>
                <RequirementSemiBold>{t('DEGREE_FILE_DESC_2')}</RequirementSemiBold>
                <ul>
                  <li>{t('DEGREE_FILE_REQUIREMENTS_2')}</li>
                </ul>
              </>
            </RequirementsWrapper>
            <InformationBanner text={t('DEGREE_FILE_BANNER')} />
          </Flex>
        }
      />
      <SingleFileUploader
        title={t('SOCIAL_SECURITY_FILE')}
        fieldName="socialSecurityFile"
        onSubmit={handleFileSubmit('socialSecurityFile')}
        acceptedTypes={['.pdf']}
        maxSizeInMB={10}
        initialFileId={userInfo?.socialSecurityFile?._id}
        blockAfterSubmit={isStudent}
        requirements={
          <Flex vertical gap={16}>
            <RequirementsWrapper>{t('SOCIAL_SECURITY_FILE_DESC')}</RequirementsWrapper>
            <InformationBanner text={t('SOCIAL_SECURITY_FILE_DO_NOT_ACCEPT')} />
          </Flex>
        }
      />
      <SingleFileUploader
        title={t('PHOTO')}
        fieldName="photo"
        onSubmit={handleFileSubmit('photo')}
        acceptedTypes={['.gif', '.jpg']}
        maxSizeInMB={15}
        initialFileId={userInfo?.photo?._id}
        blockAfterSubmit={isStudent}
        requirements={
          <Flex vertical gap={16}>
            <RequirementsWrapper>{t('PORTRAIT_FILE_REQUIREMENTS')}</RequirementsWrapper>
            <RequirementsWrapper>{t('PORTRAIT_FILE_FORMAT_INFO')}</RequirementsWrapper>
          </Flex>
        }
      />
      <SingleFileUploader
        title={t('SUBJECTS_TO_VALIDATE')}
        fieldName="subjectsToValidate"
        requirements={<RequirementsWrapper>{t('SUBJECTS_TO_VALIDATE_DESC')}</RequirementsWrapper>}
        onSubmit={handleFileSubmit('subjectsToValidate')}
        acceptedTypes={['.pdf']}
        maxSizeInMB={10}
        initialFileId={userInfo?.subjectsToValidate?._id}
        blockAfterSubmit={isStudent}
      />
      <SingleFileUploader
        title={t('COMPANY_TRAINING_EXEMPTION_FILE')}
        fieldName="companyTrainingExemptionFile"
        requirements={
          <RequirementsWrapper>{t('COMPANY_TRAINING_EXEMPTION_FILE_DESC')}</RequirementsWrapper>
        }
        onSubmit={handleFileSubmit('companyTrainingExemptionFile')}
        acceptedTypes={['.pdf']}
        maxSizeInMB={10}
        initialFileId={userInfo?.companyTrainingExemptionFile?._id}
        blockAfterSubmit={isStudent}
      />
      <SingleFileUploader
        title={t('NOTES_EQUIVALENCE_FILE')}
        fieldName="notesEquivalenceFile"
        onSubmit={handleFileSubmit('notesEquivalenceFile')}
        acceptedTypes={['.pdf']}
        maxSizeInMB={10}
        initialFileId={userInfo?.notesEquivalenceFile?._id}
        blockAfterSubmit={isStudent}
        requirements={<RequirementsWrapper>{t('NOTES_EQUIVALENCE_FILE_DESC')}</RequirementsWrapper>}
      />
    </Wrapper>
  );
};
